import { BACKEND_URL } from '../config/backendUrl';
/**
 * CredasIntegrationSection — Settings → Integrations card for the
 * Credas ID-verification / right-to-work API.
 *
 * Lets a tenant admin:
 *   1. Toggle the integration on/off
 *   2. Pick environment (Sandbox / Production)
 *   3. Paste their per-tenant API key (stored encrypted server-side)
 *   4. Test the connection (calls Credas /api/v2/ci/journeys)
 *   5. Pick a default Journey from a dropdown populated live from Credas
 *   6. Choose how the Bullhorn "Send to Credas" dialog sends (email / SMS)
 *      and which Bullhorn Candidate fields it reads the contact details from
 *
 * The API key is never read back to the browser — only a masked
 * `…1234` preview is shown.
 */
import { useEffect, useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import {
  ShieldCheck,
  CheckCircle2,
  XCircle,
  AlertCircle,
  RefreshCw,
  Eye,
  EyeOff,
  Send,
} from 'lucide-react';
import axios from 'axios';

const API_URL = `${BACKEND_URL}/api`;

export default function CredasIntegrationSection() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [config, setConfig] = useState({
    enabled: false,
    environment: 'demo',
    api_key_set: false,
    api_key_preview: null,
    journey_id: '',
    actor_id: 110,
    magic_link_path: '',
    webhook_url: '',
    email_subject: '{company_name} has requested you complete an Identity Verification Check',
    email_body: 'Hi {recipient_name}, {company_name} use Credas to process our Identity Verification Checks. Please complete this check here: {credas_link}',
    base_url: '',
    updated_at: null,
  });
  // Local-only state for inputs that don't round-trip
  const [apiKeyDraft, setApiKeyDraft] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [journeys, setJourneys] = useState([]);
  const [loadingJourneys, setLoadingJourneys] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  // Bullhorn Candidate fields the email / SMS mapping dropdowns offer.
  // `connected` is false when the tenant has no active Bullhorn connection,
  // which greys the dropdowns out.
  const [candidateFields, setCandidateFields] = useState({ loading: true, connected: false, email_fields: [], phone_fields: [] });

  // ── Load the Bullhorn field lists on mount ──
  useEffect(() => {
    let alive = true;
    axios
      .get(`${API_URL}/credas/candidate-fields`)
      .then(({ data }) => { if (alive) setCandidateFields({ loading: false, connected: !!data?.connected, email_fields: data?.email_fields || [], phone_fields: data?.phone_fields || [] }); })
      .catch(() => { if (alive) setCandidateFields({ loading: false, connected: false, email_fields: [], phone_fields: [] }); });
    return () => { alive = false; };
  }, []);

  // ── Load saved config on mount ──
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/credas/config`);
        if (!alive) return;
        setConfig((prev) => ({ ...prev, ...data }));
      } catch (e) {
        if (alive) toast.error('Could not load Credas settings');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // ── Auto-load journeys when a key is configured + enabled ──
  useEffect(() => {
    if (!config.api_key_set || !config.enabled) return;
    let alive = true;
    setLoadingJourneys(true);
    axios
      .get(`${API_URL}/credas/journeys`)
      .then(({ data }) => { if (alive) setJourneys(data?.journeys || []); })
      .catch(() => { if (alive) setJourneys([]); })
      .finally(() => { if (alive) setLoadingJourneys(false); });
    return () => { alive = false; };
  }, [config.api_key_set, config.enabled, config.environment]);

  const saveField = async (patch) => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API_URL}/credas/config`, patch);
      setConfig((prev) => ({ ...prev, ...data }));
      toast.success('Credas settings saved');
      // Clear the in-memory key draft after a save that included it
      if ('api_key' in patch) setApiKeyDraft('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveKey = async () => {
    if (!apiKeyDraft.trim()) {
      toast.error('Paste your Credas API key first');
      return;
    }
    await saveField({ api_key: apiKeyDraft.trim() });
  };

  const handleClearKey = async () => {
    if (!window.confirm('Remove the saved Credas API key for this workspace?')) return;
    await saveField({ api_key: '' });
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setConnectionStatus(null);
    try {
      const { data } = await axios.post(`${API_URL}/credas/test-connection`);
      if (data?.ok) {
        setConnectionStatus({ ok: true, message: `Connected — ${data.journey_count} journey(s) visible.` });
        toast.success(`Credas connection OK (${data.journey_count} journeys)`);
      } else {
        setConnectionStatus({ ok: false, message: data?.message || 'Connection failed', details: data?.details });
        toast.error(`Credas: ${data?.message || 'Connection failed'}`);
      }
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      setConnectionStatus({ ok: false, message: msg });
      toast.error(`Credas: ${msg}`);
    } finally {
      setTesting(false);
    }
  };

  // Saves the current subject/body edits first (so the test reflects what's
  // in the boxes right now, not stale persisted state) — silently, via a
  // plain PUT rather than saveField, so this doesn't also fire a "settings
  // saved" toast on top of the "test email sent" one. Then actually sends —
  // unlike Settings → Notifications' "Send Test Email", which only renders a
  // preview and never calls sendEmail.
  const handleSendTestEmail = async () => {
    setSendingTestEmail(true);
    try {
      await axios.put(`${API_URL}/credas/config`, { email_subject: config.email_subject, email_body: config.email_body });
      const { data } = await axios.post(`${API_URL}/credas/config/test-email`);
      toast.success(`Test email sent to ${data.sent_to} — check your inbox`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send test email');
    } finally {
      setSendingTestEmail(false);
    }
  };

  if (loading) {
    return (
      <Card className="neumorphic" data-testid="credas-integration-card">
        <CardContent className="p-6">
          <div className="flex items-center justify-center text-muted-foreground">
            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            Loading Credas settings…
          </div>
        </CardContent>
      </Card>
    );
  }

  const connected = config.api_key_set && config.enabled;

  return (
    <div className="neumorphic rounded-xl p-6" data-testid="credas-integration-card">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-600 to-violet-700 flex items-center justify-center">
          <ShieldCheck className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1">
          <h2 className="font-heading font-semibold text-lg">Credas ID Verification</h2>
          <p className="text-sm text-muted-foreground">
            Automate right-to-work and ID checks. We create the Credas process via API,
            suppress their email, and send the candidate their link ourselves using the template below.
          </p>
        </div>
        {connected ? (
          <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 rounded-full" data-testid="credas-status-connected">
            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
            <span className="text-sm font-medium text-emerald-700">Active</span>
          </div>
        ) : config.api_key_set ? (
          <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 rounded-full">
            <AlertCircle className="w-4 h-4 text-amber-600" />
            <span className="text-sm font-medium text-amber-700">Key saved · disabled</span>
          </div>
        ) : (
          <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 rounded-full">
            <XCircle className="w-4 h-4 text-slate-500" />
            <span className="text-sm font-medium text-slate-600">Not configured</span>
          </div>
        )}
      </div>

      {/* Enable toggle */}
      <div className="flex items-center justify-between py-3 border-b border-slate-200">
        <div>
          <Label className="text-sm font-medium">Enable Credas for this workspace</Label>
          <p className="text-xs text-muted-foreground mt-0.5">
            When OFF, the &quot;Run Credas check&quot; checkbox and &quot;Send to Credas&quot; button are hidden everywhere.
          </p>
        </div>
        <Switch
          checked={!!config.enabled}
          onCheckedChange={(v) => saveField({ enabled: v })}
          disabled={saving}
          data-testid="credas-enable-switch"
        />
      </div>

      {/* Environment */}
      <div className="py-3 border-b border-slate-200">
        <Label className="text-sm font-medium">Environment</Label>
        <div className="mt-2 flex gap-2">
          {[
            { key: 'demo', label: 'Sandbox', hint: 'portal.credasdemo.com' },
            { key: 'production', label: 'Production', hint: 'portal.credas.com' },
          ].map((env) => {
            const active = config.environment === env.key;
            return (
              <button
                key={env.key}
                type="button"
                disabled={saving}
                onClick={() => saveField({ environment: env.key })}
                className={`flex-1 px-3 py-2 rounded-lg border text-left transition-colors ${
                  active
                    ? 'border-indigo-500 bg-indigo-50'
                    : 'border-slate-200 hover:border-slate-300 bg-white'
                }`}
                data-testid={`credas-env-${env.key}`}
              >
                <div className="text-sm font-medium">{env.label}</div>
                <div className="text-[11px] text-muted-foreground font-mono">{env.hint}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* API key */}
      <div className="py-3 border-b border-slate-200">
        <Label className="text-sm font-medium">API Key</Label>
        <p className="text-xs text-muted-foreground mt-0.5 mb-2">
          Generate from the Credas portal → Admin Portal → your user → Issue API Key.
          Stored encrypted (AES) in our database; never returned in plain text.
        </p>
        {config.api_key_set ? (
          <div className="flex items-center gap-2">
            <div className="flex-1 px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 text-sm font-mono text-slate-600">
              {config.api_key_preview || '••••••••'}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleClearKey}
              disabled={saving}
              data-testid="credas-clear-key-btn"
            >
              Remove
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Input
                type={showKey ? 'text' : 'password'}
                placeholder="Paste your Credas API key…"
                value={apiKeyDraft}
                onChange={(e) => setApiKeyDraft(e.target.value)}
                className="pr-9 font-mono text-sm"
                data-testid="credas-api-key-input"
              />
              <button
                type="button"
                onClick={() => setShowKey((s) => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                tabIndex={-1}
              >
                {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <Button
              size="sm"
              onClick={handleSaveKey}
              disabled={saving || !apiKeyDraft.trim()}
              data-testid="credas-save-key-btn"
            >
              Save key
            </Button>
          </div>
        )}
        {config.api_key_set && (
          <div className="flex items-center gap-2 mt-3">
            <Button
              size="sm"
              variant="outline"
              onClick={handleTestConnection}
              disabled={testing || !config.enabled}
              data-testid="credas-test-btn"
            >
              {testing ? (
                <><RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Testing…</>
              ) : (
                'Test connection'
              )}
            </Button>
            {connectionStatus && (
              <span className={`text-xs ${connectionStatus.ok ? 'text-emerald-700' : 'text-red-700'}`}>
                {connectionStatus.ok ? '✓ ' : '✗ '}{connectionStatus.message}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Journey */}
      <div className="py-3 border-b border-slate-200">
        <Label className="text-sm font-medium">Default Journey</Label>
        <p className="text-xs text-muted-foreground mt-0.5 mb-2">
          The Credas journey (e.g. &quot;Right to Work — standard&quot;) used when a recruiter
          ticks &quot;Run Credas check&quot; without picking a specific one.
        </p>
        {loadingJourneys ? (
          <div className="text-xs text-muted-foreground flex items-center">
            <RefreshCw className="w-3 h-3 mr-1.5 animate-spin" />
            Fetching journeys from Credas…
          </div>
        ) : journeys.length > 0 ? (
          <select
            className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm"
            value={config.journey_id || ''}
            onChange={(e) => saveField({ journey_id: e.target.value })}
            disabled={saving}
            data-testid="credas-journey-select"
          >
            <option value="">— Select a journey —</option>
            {journeys.map((j) => (
              <option key={j.id} value={j.id}>
                {j.name}
              </option>
            ))}
          </select>
        ) : (
          <div className="space-y-2">
            <Input
              type="text"
              placeholder="Paste journey GUID directly…"
              value={config.journey_id || ''}
              onChange={(e) => setConfig((p) => ({ ...p, journey_id: e.target.value }))}
              onBlur={(e) => saveField({ journey_id: e.target.value })}
              className="font-mono text-sm"
              data-testid="credas-journey-input"
            />
            <p className="text-[11px] text-muted-foreground">
              {config.api_key_set
                ? "Couldn't auto-fetch journeys — Credas returned an empty list. Paste the GUID manually or check the Sandbox/Production toggle above."
                : "Save your API key first to auto-populate this list."}
            </p>
          </div>
        )}
      </div>

      {/* How the Bullhorn "Send to Credas" dialog sends the check, and which
          Bullhorn Candidate fields it reads the contact details from. */}
      <div className="py-3 border-b border-slate-200" data-testid="credas-send-options">
        <Label className="text-sm font-medium">Sending from Bullhorn</Label>
        <p className="text-xs text-muted-foreground mt-0.5 mb-3">
          What the &quot;Send to Credas&quot; dialog in Bullhorn starts with. A box is still left
          unticked (and greyed out) when the candidate has nothing in the mapped field below.
        </p>
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <Label className="text-sm font-normal">Tick &quot;Send via Email&quot; by default</Label>
            <Switch
              checked={config.send_email_default !== false}
              onCheckedChange={(v) => saveField({ send_email_default: v })}
              disabled={saving}
              data-testid="credas-default-email-switch"
            />
          </div>
          <div className="flex items-center justify-between gap-3">
            <Label className="text-sm font-normal">Tick &quot;Send via SMS&quot; by default</Label>
            <Switch
              checked={config.send_sms_default === true}
              onCheckedChange={(v) => saveField({ send_sms_default: v })}
              disabled={saving}
              data-testid="credas-default-sms-switch"
            />
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-3 mt-4">
          {[
            { key: 'email_field', label: 'Bullhorn field for email', options: candidateFields.email_fields, fallback: 'email', testid: 'credas-email-field-select' },
            { key: 'phone_field', label: 'Bullhorn field for SMS (phone)', options: candidateFields.phone_fields, fallback: 'mobile', testid: 'credas-phone-field-select' },
          ].map((f) => {
            const current = config[f.key] || f.fallback;
            // Keep the saved choice visible even when the list couldn't be loaded.
            const options = f.options.some((o) => o.name === current) ? f.options : [{ name: current, label: current }, ...f.options];
            return (
              <div key={f.key}>
                <Label className="text-xs font-medium text-muted-foreground">{f.label}</Label>
                <select
                  className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed"
                  value={current}
                  onChange={(e) => saveField({ [f.key]: e.target.value })}
                  disabled={saving || candidateFields.loading || !candidateFields.connected}
                  data-testid={f.testid}
                >
                  {options.map((o) => (
                    <option key={o.name} value={o.name}>{o.label}</option>
                  ))}
                </select>
              </div>
            );
          })}
        </div>
        {candidateFields.loading ? (
          <p className="text-[11px] text-muted-foreground mt-2 flex items-center">
            <RefreshCw className="w-3 h-3 mr-1.5 animate-spin" />
            Loading Bullhorn fields…
          </p>
        ) : !candidateFields.connected && (
          <p className="text-xs text-amber-700 mt-2" data-testid="credas-field-mapping-no-bullhorn">
            You must have an active Bullhorn connection to map these fields
          </p>
        )}
      </div>

      {/* Candidate email */}
      <div className="py-3 border-b border-slate-200">
        <div className="flex items-start justify-between gap-3">
          <div>
            <Label className="text-sm font-medium">Candidate Email</Label>
            <p className="text-xs text-muted-foreground mt-0.5 mb-2">
              Sent automatically to the candidate as soon as their Credas link is ready. Available
              merge tags: <code className="text-xs bg-muted px-1 rounded">{'{company_name}'}</code>{' '}
              <code className="text-xs bg-muted px-1 rounded">{'{recipient_name}'}</code>{' '}
              <code className="text-xs bg-muted px-1 rounded">{'{credas_link}'}</code>.
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleSendTestEmail}
            disabled={sendingTestEmail}
            className="shrink-0"
            data-testid="credas-send-test-email-button"
          >
            {sendingTestEmail ? (
              <><RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" />Sending…</>
            ) : (
              <><Send className="w-3.5 h-3.5 mr-1.5" />Send Test Email</>
            )}
          </Button>
        </div>
        <div className="space-y-2">
          <div>
            <Label className="text-xs font-medium text-muted-foreground">Subject</Label>
            <Input
              type="text"
              value={config.email_subject}
              onChange={(e) => setConfig((p) => ({ ...p, email_subject: e.target.value }))}
              onBlur={(e) => saveField({ email_subject: e.target.value })}
              className="text-sm mt-1"
              data-testid="credas-email-subject-input"
            />
          </div>
          <div>
            <Label className="text-xs font-medium text-muted-foreground">Body</Label>
            <Textarea
              value={config.email_body}
              onChange={(e) => setConfig((p) => ({ ...p, email_body: e.target.value }))}
              onBlur={(e) => saveField({ email_body: e.target.value })}
              rows={4}
              className="text-sm mt-1"
              data-testid="credas-email-body-input"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
